// Shared "Revised A" Payment Schedule card.
//
// Renders the same two-card layout as the install agreement PDF
// (installAgreementPDF.ts). Used by:
//   - StatusPortal Payment tab (live HO view — supplements appear here ONLY
//     once the carrier approves them; a pending request is mentioned in a
//     note but never added to a total — Sep 15, 2026)
//   - Anywhere else we want the schedule to render on screen
//
// Both cards reflect:
//   First Payment (before build)  — orange, From Insurance + From Homeowner
//   Depreciation Settlement (after build) — teal, From Insurance only
//   Initial Invoice Total — slate bar across the bottom
// Oct 29, 2026: when trades are billed to the homeowner from the trade sheet,
// the after-build card becomes the Final Payment — the carrier's depreciation
// plus a "Trades billed to you (due at completion)" block, one line per trade —
// exactly as the signed agreement prints it. The trades are never in the first
// payment.
//
// The deal must carry the scope-calc columns (rcv_total, depreciation_total,
// deductible, supplements, pwi_code, upgrades, retail_profit, at_cost_retail,
// acv_credit, dep_credit, misc_credit, non_commissionable, acv_only, and since
// Oct 29, 2026 trade_ho_total / trade_ho_floor / trade_ho_lines).
// HomeownerDeal exposes all of these (ho_resolve_deal returns the whole row).

import { calcScope } from "../../lib/scopeCalc";
import { afterBuildCredit, tradeMoneyOf } from "../../lib/agreementPayload";

const fmt = (n: number) => {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

interface Props {
  // Permissive shape — anything with the right scope columns calcs correctly.
  // Both Deal and HomeownerDeal satisfy this.
  deal: Record<string, unknown>;
  /** Render with a card border + Section-style padding. Default true. */
  bordered?: boolean;
}

export default function PaymentScheduleCard({ deal, bordered = true }: Props) {
  const calc = calcScope(deal);
  const trades = tradeMoneyOf(calc);
  const hasTrades = trades.total > 0;

  // ── Retail (Jun 11, 2026): 50% deposit / 50% on completion ──────────────
  // No insurance settlements — the homeowner sees the simple retail schedule
  // instead of the two-settlement breakdown below.
  if (calc.isRetail) {
    if (calc.retailContractTotal <= 0) {
      return (
        <div className="rounded-lg bg-bardin-sky/30 border border-slate-200 p-5 text-center">
          <div className="text-4xl mb-2">📋</div>
          <div className="text-sm font-semibold text-bardin-ink mb-1">Payment schedule pending</div>
          <p className="text-xs text-slate-500 max-w-xs mx-auto leading-snug">
            Your payment schedule will appear here once your contract is finalized. Your Restore Manager will reach out with next steps.
          </p>
        </div>
      );
    }
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* DEPOSIT card */}
          <div className="rounded-lg border border-bardin-orange bg-orange-50 p-3">
            <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">Before Build</div>
            <div className="text-base font-bold text-bardin-ink mt-0.5 mb-2">50% Deposit</div>
            <p className="text-[10.5px] leading-snug text-slate-500">
              Due at signing or before material delivery, whichever is later.
            </p>
            <div className="mt-3 pt-2 border-t border-bardin-orange/40 flex items-baseline justify-between">
              <span className="text-sm font-bold text-bardin-ink">Deposit</span>
              <span className="text-2xl font-extrabold text-bardin-orange">{fmt(calc.retailDeposit)}</span>
            </div>
          </div>
          {/* COMPLETION card */}
          <div className="rounded-lg border border-bardin-teal bg-teal-50 p-3">
            <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">After Build</div>
            <div className="text-base font-bold text-bardin-ink mt-0.5 mb-2">{hasTrades ? "Final Payment" : "50% Final Payment"}</div>
            <p className="text-[10.5px] leading-snug text-slate-500">
              Due upon completion of the work.
            </p>
            {hasTrades && (
              <div className="mt-2">
                <SubSection
                  label="Due at completion"
                  rows={[
                    { label: "50% of the contract", value: fmt(calc.retailContractTotal - calc.retailDeposit) },
                    ...trades.lines.map((t) => ({ label: `+ ${t.label}`, value: `+ ${fmt(t.amount)}` })),
                  ]}
                  subtotalLabel="Final payment"
                  subtotalValue={fmt(calc.retailCompletionPayment)}
                  accent="teal"
                />
              </div>
            )}
            <div className="mt-3 pt-2 border-t border-bardin-teal/40 flex items-baseline justify-between">
              <span className="text-sm font-bold text-bardin-ink">Final Payment</span>
              <span className="text-2xl font-extrabold text-bardin-teal">{fmt(calc.retailCompletionPayment)}</span>
            </div>
          </div>
        </div>
        {/* Total bar */}
        <div className="mt-3 rounded-lg bg-bardin-ink text-white px-4 py-3 flex items-baseline justify-between">
          <span className="text-sm font-bold">{hasTrades ? "Total (contract + trades)" : "Contract Total"}</span>
          <span className="text-lg font-extrabold">{fmt(hasTrades ? calc.initialInvoice : calc.retailContractTotal)}</span>
        </div>
      </div>
    );
  }

  // Empty state: scope hasn't been finalized yet (no RCV/ACV/Deductible).
  // The HO portal Payment tab can be reached before the install agreement
  // exists, so showing all-$0 cards would be misleading. Guard surfaces a
  // friendly placeholder instead. Apr 30, 2026.
  const scopeFinalized = calc.rcv > 0 || calc.acv > 0 || calc.deductible > 0;
  if (!scopeFinalized) {
    return (
      <div className="rounded-lg bg-bardin-sky/30 border border-slate-200 p-5 text-center">
        <div className="text-4xl mb-2">📋</div>
        <div className="text-sm font-semibold text-bardin-ink mb-1">Payment schedule pending</div>
        <p className="text-xs text-slate-500 max-w-xs mx-auto leading-snug">
          Your payment schedule will appear here once your insurance scope is finalized. Your Restore Manager will reach out with next steps.
        </p>
      </div>
    );
  }

  // Build the row lists the same way the PDF does, dropping zero-rows so the
  // card stays compact when most fields are zero.
  // May 7, 2026: ACV Credit no longer subtracted from the insurance side.
  // The homeowner endorses the full ACV check (ACV − Deductible) to Bardin.
  // The un-performed ACV value only credits the deductible on the HO side.
  type R = { label: string; value: string };
  const beforeInsRows: R[] = [
    { label: "ACV", value: fmt(calc.acv) },
    { label: "− Deductible", value: `− ${fmt(calc.deductible)}` },
  ];

  const beforeHoRows: R[] = [
    { label: "Deductible", value: fmt(calc.deductible) },
  ];
  // Homeowner-paid extras are part of the out-of-pocket subtotal (hoGross), so
  // they MUST be itemized here or the breakdown won't sum to "Homeowner
  // portion". Jun 22, 2026: at-cost retail / upgrades / retail add-ons were
  // missing — a non-zero at_cost_retail (e.g. $850) showed up only as an
  // unexplained gap between the Deductible row and the subtotal.
  if (calc.upgrades > 0) beforeHoRows.push({ label: "+ Upgrades", value: `+ ${fmt(calc.upgrades)}` });
  if (calc.retailProfit > 0) beforeHoRows.push({ label: "+ Retail add-ons", value: `+ ${fmt(calc.retailProfit)}` });
  if (calc.atCostRetail > 0) beforeHoRows.push({ label: "+ Retail (at-cost trades)", value: `+ ${fmt(calc.atCostRetail)}` });
  // Oct 30, 2026 (scope gate): past the deductible the first payment takes the credits only up to it, so one capped
  // row keeps this card adding up; the rest is "Credit beyond the deductible" in the after-build card.
  if (calc.creditOverage > 0) {
    beforeHoRows.push({ label: "− Credits (up to your deductible)", value: `− ${fmt(calc.creditsApplied)}` });
  } else {
    if (calc.acvCredit > 0) beforeHoRows.push({ label: "− ACV credit (un-performed)", value: `− ${fmt(calc.acvCredit)}` });
    if (calc.miscCredit > 0) beforeHoRows.push({ label: "− Misc credit", value: `− ${fmt(calc.miscCredit)}` });
  }

  const afterInsRows: R[] = calc.acvOnly ? [] : [
    { label: "Recoverable Depreciation", value: fmt(calc.dep) },
  ];
  if (!calc.acvOnly && calc.depCredit > 0) afterInsRows.push({ label: "− Un-performed depreciation", value: `− ${fmt(calc.depCredit)}` });
  // Approved supplements only. calc.supplements is 0 while a request is still
  // pending with the carrier (Sep 15, 2026 — the pending amount used to be
  // added here and inflated the Initial Invoice Total by the full ask; it is
  // now mentioned in the note below and never summed).
  if (!calc.acvOnly && calc.supplements > 0) {
    afterInsRows.push({ label: "+ Approved supplements", value: `+ ${fmt(calc.supplements)}` });
  }
  if (!calc.acvOnly && calc.pwi > 0) afterInsRows.push({ label: "+ PWI (paid when invoiced)", value: `+ ${fmt(calc.pwi)}` });
  // Oct 29, 2026 — the trades billed to the homeowner, due at completion. A
  // credit beyond the deductible (capped out of the first payment) comes off
  // here, so the card sums to the completion payment like the agreement's: off
  // the trades first, never below $0, the rest off the insurance portion (Oct
  // 30: a $500 credit on $200 of trades read "Homeowner portion −$300").
  // Oct 30, 2026 (scope gate): with no trades too, so the cards add up to the Initial Invoice Total.
  const credit = afterBuildCredit(calc.creditOverage, trades.total, calc.acvOnly ? 0 : calc.depSettlement);
  if (credit.onInsurance > 0) afterInsRows.push({ label: "− Credit beyond the deductible", value: `− ${fmt(credit.onInsurance)}` });
  const afterHoRows: R[] = trades.lines.map((t) => ({ label: `+ ${t.label}`, value: `+ ${fmt(t.amount)}` }));
  if (credit.onTrades > 0) {
    afterHoRows.push({ label: "− Credit beyond the deductible", value: `− ${fmt(credit.onTrades)}` });
  }

  return (
    <div className={bordered ? "" : ""}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* BEFORE BUILD card */}
        <div className="rounded-lg border border-bardin-orange bg-orange-50 p-3">
          <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">Before Build</div>
          <div className="text-base font-bold text-bardin-ink mt-0.5 mb-3">First Payment to Bardin</div>

          <SubSection
            label="From Insurance (HO endorses to Bardin)"
            rows={beforeInsRows}
            subtotalLabel="Insurance portion"
            subtotalValue={fmt(calc.acvSettlementToBardin)}
            accent="orange"
          />
          <div className="h-1.5" />
          <SubSection
            label="From Homeowner (Out-of-Pocket)"
            rows={beforeHoRows}
            subtotalLabel="Homeowner portion"
            subtotalValue={fmt(calc.hoOutOfPocket)}
            accent="orange"
          />

          <div className="mt-3 pt-2 border-t border-bardin-orange/40 flex items-baseline justify-between">
            <span className="text-sm font-bold text-bardin-ink">First Payment</span>
            <span className="text-2xl font-extrabold text-bardin-orange">{fmt(calc.firstPaymentToBardin)}</span>
          </div>
        </div>

        {/* AFTER BUILD card */}
        <div className="rounded-lg border border-bardin-teal bg-teal-50 p-3">
          <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">After Build</div>
          <div className="text-base font-bold text-bardin-ink mt-0.5 mb-3">{hasTrades ? "Final Payment to Bardin" : "Depreciation Settlement"}</div>

          {calc.acvOnly ? (
            <div className="text-xs text-slate-600">
              ACV only — depreciation is not recoverable on this claim.
            </div>
          ) : (
            <>
              <SubSection
                label="From Insurance (released after invoice)"
                rows={afterInsRows}
                subtotalLabel="Insurance portion"
                subtotalValue={fmt(calc.depSettlement - credit.onInsurance)}
                accent="teal"
              />
              <p className="mt-2 text-[10.5px] leading-snug text-slate-500">
                {hasTrades
                  ? "The carrier releases recoverable depreciation directly to Bardin once the work is invoiced. The trades billed to you are due at completion."
                  : "No homeowner contribution at this stage. The carrier releases recoverable depreciation directly to Bardin once the work is invoiced."}
                {credit.onTrades + credit.onInsurance > 0 && " The credit beyond your deductible comes off this payment."}
              </p>
              {calc.supplementsPending && (
                <div className="mt-2 rounded bg-amber-50 border border-amber-300 p-2 text-[10.5px] leading-snug text-amber-900">
                  <div className="font-bold mb-0.5">⏳ Supplement pending with your insurance company</div>
                  <div>
                    A supplement request has been submitted to your carrier. It is not included in the amounts above. Once the carrier approves it, the approved amount will be added to this settlement and your Initial Invoice Total will update.
                  </div>
                </div>
              )}
              {calc.supplements > 0 && !calc.supplementsPending && (
                <div className="mt-2 rounded bg-white border border-teal-200 p-2 text-[10.5px] leading-snug text-bardin-teal">
                  ✓ Approved supplement of {fmt(calc.supplements)} added to this settlement.
                </div>
              )}
            </>
          )}

          {hasTrades && (
            <>
              <div className="h-1.5" />
              <SubSection
                label="Trades billed to you (due at completion)"
                rows={afterHoRows}
                subtotalLabel="Homeowner portion"
                subtotalValue={fmt(trades.total - credit.onTrades)}
                accent="teal"
              />
            </>
          )}

          <div className="mt-3 pt-2 border-t border-bardin-teal/40 flex items-baseline justify-between">
            <span className="text-sm font-bold text-bardin-ink">{hasTrades ? "Final Payment" : "Depreciation"}</span>
            <span className="text-2xl font-extrabold text-bardin-teal">{fmt(hasTrades ? trades.completionPayment : calc.acvOnly ? 0 : calc.depSettlement - credit.onInsurance)}</span>
          </div>
        </div>
      </div>

      {/* Total bar */}
      <div className="mt-3 rounded-lg bg-bardin-ink text-white px-4 py-3 flex items-baseline justify-between">
        <span className="text-sm font-bold">Initial Invoice Total</span>
        <span className="text-lg font-extrabold">{fmt(calc.initialInvoice)}</span>
      </div>

      {/* Non-collect revenue is intentionally NOT shown to the homeowner
          (Jun 19, 2026, per Greg). The full invoice amount is what the customer
          is billed and owes; Bardin's decision not to pursue collections on the
          non-collect portion is an internal stance only. Non-collect stays
          visible on staff surfaces (ScopeSection, Commission Board). */}
    </div>
  );
}

/* ─── Sub-section block ───────────────────────────────────────────── */

interface SubSectionProps {
  label: string;
  rows: { label: string; value: string }[];
  subtotalLabel: string;
  subtotalValue: string;
  accent: "orange" | "teal";
}

function SubSection({ label, rows, subtotalLabel, subtotalValue, accent }: SubSectionProps) {
  const accentClass = accent === "orange" ? "text-bardin-orange" : "text-bardin-teal";
  return (
    <div className="rounded bg-white/70 border border-slate-200 px-2.5 py-2">
      <div className="text-[9px] font-bold tracking-wider text-slate-500 uppercase mb-1">{label}</div>
      <div className="space-y-0.5 text-[11px] text-bardin-ink">
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between gap-2">
            <span className="min-w-0">{r.label}</span>
            <span className="shrink-0">{r.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-1 pt-1 border-t border-dashed border-slate-300 flex justify-between">
        <span className={`text-[11px] font-bold ${accentClass}`}>{subtotalLabel}</span>
        <span className={`text-[11px] font-bold ${accentClass}`}>{subtotalValue}</span>
      </div>
    </div>
  );
}
